/**
 * Integrytas Group - Common Security Module (CSM)
 * i18next bootstrap (v14).
 *
 * Screen prompts ship as JSON bundles in src/locales/ (en, es) — the static
 * DEFAULTS. The CSM standard uses full locale codes ('EN_US', 'ES_MX', 'EN_GB',
 * matching csm_language_master.internal_code and csm_user_master.preferred_locale).
 *
 * i18next is keyed by the FULL CSM locale code, so EN_US and EN_GB can carry
 * different prompts. Each CSM locale falls back to its static base bundle ('en' or
 * 'es') and then English. Per-locale OVERRIDES (and prompts for languages created
 * at runtime) live in csm_i18n_string and are hydrated over the defaults by
 * loadI18nOverrides() — that's how a deployed instance renames 'Customer Name' to
 * 'Client Name', even in its own home language.
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { apiDb } from '../lib/api_client';

import en from '../locales/en.json';
import es from '../locales/es.json';
// EN_GB (§8 baseline): British-spelling OVERRIDE layer, deep-merged over en for the
// EN_GB locale only. Generated from en.json by scripts/gen_en_gb.mjs.
import enGb from '../locales/en_gb.json';
// App-owned prompt extension (§16): derived apps populate src/app/app_locale_en.json
// (via `i18n_extract.mjs --app`) instead of editing the inherited en.json. Empty in BaseApp.
import appEn from '../app/app_locale_en.json';

export const DEFAULT_CSM_LOCALE = 'EN_US';

/** Normalise to a full CSM locale code (e.g. 'en-gb' → 'EN_GB'); default EN_US. */
export function csmLngTag(csmLocale: string | null | undefined): string {
  if (!csmLocale) return DEFAULT_CSM_LOCALE;
  return csmLocale.replace(/-/g, '_').toUpperCase();
}

/** The static base bundle ('en' | 'es') a CSM locale falls back to. */
function baseLng(code: string): 'en' | 'es' {
  return code.toLowerCase().startsWith('es') ? 'es' : 'en';
}

/** Back-compat: the 2-letter base tag for a CSM locale. */
export function csmLocaleToI18n(csmLocale: string | null | undefined): string {
  return baseLng(csmLngTag(csmLocale));
}

/** Full CSM locale code → BCP-47 tag, for Intl.* APIs and the HTML `lang`
 *  attribute. This is what actually drives day/month order in
 *  Intl.DateTimeFormat and native <input type="date"> pickers (§11).
 *
 *  Most codes are the plain LL_CC pair and need no entry — `EN_GB` splits to
 *  `en-GB` unaided. This table is for the ones that do NOT map by splitting:
 *  a locale whose real tag carries a variant subtag, such as Scottish Standard
 *  English (`en-GB-scotland`), which cannot be spelled in ten characters of
 *  LL_CC and so is stored as `EN_GB_SCOT`. Registering a locale here is what
 *  makes Intl name it correctly instead of guessing from the code. */
export const CSM_BCP47: Record<string, string> = {
  EN_US: 'en-US',
  EN_GB: 'en-GB',
  ES_MX: 'es-MX',
  EN_GB_SCOT: 'en-GB-scotland',
};
export function csmLocaleToBcp47(csmLocale: string | null | undefined): string {
  return CSM_BCP47[csmLngTag(csmLocale)] ?? CSM_BCP47[DEFAULT_CSM_LOCALE];
}

/** Keep the document's `lang` attribute in step with the active CSM locale, so
 *  native date inputs/pickers render day/month/year in the browser's own
 *  locale-appropriate order (§11) without per-input plumbing. */
function syncDocumentLang(csmLocale: string): void {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = csmLocaleToBcp47(csmLocale);
}

let initialized = false;

export function initI18n(): typeof i18n {
  if (initialized) return i18n;
  void i18n
    .use(initReactI18next)
    .init({
      resources: {
        en: { translation: en },
        es: { translation: es },
      },
      lng: DEFAULT_CSM_LOCALE,
      // Each CSM locale falls back to its base static bundle, then English.
      // DB overrides are layered onto the CSM-locale bundle by loadI18nOverrides().
      fallbackLng: (code?: string) => (baseLng(code ?? DEFAULT_CSM_LOCALE) === 'es' ? ['es', 'en'] : ['en']),
      cleanCode: false,
      interpolation: { escapeValue: false },
      returnEmptyString: false,
    });
  // Deep-merge the app-owned prompt bundle over the English base (no-op when empty).
  i18n.addResourceBundle('en', 'translation', appEn, true, true);
  // EN_GB carries British spellings over the en base (§8): register the override
  // layer under the full locale tag so EN_GB resolves enGb → en, EN_US stays en.
  i18n.addResourceBundle('EN_GB', 'translation', enGb, true, true);
  syncDocumentLang(DEFAULT_CSM_LOCALE);
  initialized = true;
  return i18n;
}

/** Switch the active language using a CSM locale code. */
export function setActiveCsmLocale(csmLocale: string | null | undefined): void {
  initI18n();
  const code = csmLngTag(csmLocale);
  syncDocumentLang(code);
  void i18n.changeLanguage(code);
}

/**
 * Hydrate per-locale screen-prompt overrides from csm_i18n_string over the static
 * defaults, then re-activate the locale so the React tree re-renders. Best-effort:
 * if the fetch fails the static defaults still apply. Call after login and whenever
 * the active locale changes.
 */
export async function loadI18nOverrides(csmLocale: string | null | undefined): Promise<void> {
  initI18n();
  const code = csmLngTag(csmLocale);
  try {
    const { data, error } = await apiDb
      .from('csm_i18n_string')
      .select('string_key, display_text')
      .eq('locale_code', code);
    if (!error) {
      for (const row of (data ?? []) as { string_key: string; display_text: string }[]) {
        i18n.addResource(code, 'translation', row.string_key, row.display_text);
      }
    }
  } catch {
    /* overrides are best-effort */
  }
  syncDocumentLang(code);
  await i18n.changeLanguage(code);
}

export { i18n };
